import { AppSetting } from './Types/AppSetting.types';
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, MenuItem } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { Divider, FlexLayout, GridLayout } from '@Styled/utilities';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { SETTINGS_SCHEMA } from './Schema/AppSettings.schema';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import AppSettingsApi, {
	AppSettingsRequester,
} from './Services/AppSettings.requester';
import { Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import EdNumericInput from '@Components/UI/Inputs/EdNumericInput/EdNumericInput';
import { EdFormControlLabel } from '@Components/UI/Inputs/EdFormControlLabel/EdFormControlLabel';

type Props = {
	data: AppSetting;
};

const SettingsForm: FC<Props> = ({ data }) => {
	const { displaySnackbar } = useSnackbar();
	const history = useHistory();

	const defaultValues = React.useMemo<AppSetting | undefined>(() => {
		if (data) {
			const defaultT: AppSetting = {
				aman_accept: data?.aman_accept,
				credit_card_accept: data?.credit_card_accept,
				credit_card_opay: data?.credit_card_opay,
				fawry_moneyhash: data?.fawry_moneyhash,
				credit_card_moneyhash: data?.credit_card_moneyhash,
				ewallet_moneyhash: data?.ewallet_moneyhash,
				moneyhash_flow_id_fawry: data?.moneyhash_flow_id_fawry,
				moneyhash_flow_id_card: data?.moneyhash_flow_id_card,
				moneyhash_flow_id_ewallet: data?.moneyhash_flow_id_ewallet,
				credit_card_kashier: data?.credit_card_kashier,
				ewallet_kashier: data?.ewallet_kashier,
				manual_vc_enabled: data?.manual_vc_enabled ?? false,
				manual_vc_receiver: data?.manual_vc_receiver ?? null,
				device_limit: data?.device_limit,
				device_verification_required: data?.device_verification_required,
				device_limit_enforced: data?.device_limit_enforced,
				max_concurrent_sessions: data?.max_concurrent_sessions,
				ewallet_accept: data?.ewallet_accept,
				ewallet_fawry: data?.ewallet_fawry,
				ewallet_opay: data?.ewallet_opay,
				exams_section: data?.exams_section,
				fawry_fawry: data?.fawry_fawry,
				masary_accept: data?.masary_accept,
				parent_portal: data?.parent_portal,
				phone_verification_required: data?.phone_verification_required,
				recharge_card: data?.recharge_card,
				id: data?.id,
				registration_fees: data?.registration_fees,
				revision_section: data?.revision_section,
				surcharge_registration_fees: data?.surcharge_registration_fees,
				theme: data.theme,
				landing_headline: data.landing_headline,
				landing_subheadline: data.landing_subheadline,
				instructor_section: data.instructor_section,
				subjects_section: data.subjects_section,
				classrooms_enabled: data.classrooms_enabled,
				standalone_courses_enabled: data.standalone_courses_enabled,
				bundles_enabled: data.bundles_enabled,
				waselchat_widget: data.waselchat_widget,
				// `?? true` matches the columns' DB default: on a backend that has
				// not run the migration yet these arrive undefined, and this form
				// PUTs the whole object — without it the save would write false.
				unit_notes_enabled: data.unit_notes_enabled ?? true,
				unit_qa_enabled: data.unit_qa_enabled ?? true,
				about_us_page: data.about_us_page,
				terms_link: data.terms_link,
				privacy_link: data.privacy_link,
				fb_link: data.fb_link,
				youtube_link: data.youtube_link,
				linkedin_link: data.linkedin_link,
				instagram_link: data.instagram_link,
				video_percentages: data.video_percentages,
				app_wallet: data.app_wallet,
				meta_title: data.meta_title,
				// Must be listed here or the field starts undefined and the PUT
				// (which sends the whole form object) silently drops it.
				sms_provider: data.sms_provider,
				cequens_api_key: data.cequens_api_key,
				cequens_username: data.cequens_username,
				cequens_sender_name: data.cequens_sender_name,
			};
			return defaultT;
		}
	}, [data]);

	const { control, formState, watch, handleSubmit } = useForm<AppSetting>({
		resolver: yupResolver(SETTINGS_SCHEMA),
		mode: 'all',
		defaultValues: defaultValues,
	});

	const watchPhone = watch('phone_verification_required');
	const watchDeviceVerification = watch('device_verification_required');
	const watchDeviceLimitEnforced = watch('device_limit_enforced');
	const watchRevision = watch('revision_section');
	const watchExam = watch('exams_section');
	const watchRecharge = watch('recharge_card');
	const watchCredit = watch('credit_card_accept');
	const watchFawry = watch('fawry_fawry');
	const watchWallet = watch('ewallet_accept');
	const watchWalletFawry = watch('ewallet_fawry');
	const watchOpay = watch('credit_card_opay');
	const watchAman = watch('aman_accept');
	const watchWalletOpay = watch('ewallet_opay');
	const watchMasay = watch('masary_accept');
	const watchFawryMoneyHash = watch('fawry_moneyhash');
	const watchCardMoneyHash = watch('credit_card_moneyhash');
	const watchWalletMoneyHash = watch('ewallet_moneyhash');
	const watchCardKashier = watch('credit_card_kashier');
	const watchWalletKashier = watch('ewallet_kashier');

	const onSave = async (form: AppSetting) => {
		try {
			await AppSettingsRequester.getInstance().update(form);
			displaySnackbar('success', `Settings Updated successfully`);
		} catch (e) {
			const msg = e?.response?.data?.message || 'Unable to Update Settings ';
			displaySnackbar('error', msg);
		}
	};

	return (
		<SectionLayout
			gridGap={'2rem'}
			gridTemplateColumns={{ sm: 'repeat(1,1fr)' }}
			pb="8rem"
			minHeight="100vh"
			width="80%"
			key={data.id}
		>
			<Divider label="Sign up process" mb="0" pb="0" />
			<FlexLayout
				display="flex"
				alignItems="center"
				gridGap="0.938rem"
				mt="2.625rem"
			>
				<Controller
					name="phone_verification_required"
					control={control}
					render={({ field }) => (
						<FlexLayout
							display="flex"
							alignItems="center"
							gridGap="0.938rem"
							mt="0.625rem"
						>
							<StatusBox
								checked={watchPhone ? true : false}
								{...field}
								id="Status"
								defaultChecked={false}
							/>
							<LabelStyle>Phone verification required</LabelStyle>
						</FlexLayout>
					)}
				/>
			</FlexLayout>
			<FlexLayout justifyContent="space-between" alignItems="center">
				<EdFormControl
					control={control}
					width="4.794rem"
					type="number"
					inputMode="numeric"
					name={`registration_fees`}
					label="Registeration Fees"
					required
				/>
				<EdFormControlStyle
					control={control}
					width="7.794rem"
					type="number"
					inputMode="numeric"
					name={`surcharge_registration_fees`}
					label="Surcharge Registeration fees"
					required
				/>
			</FlexLayout>
			<Divider label="Home Page" mb="0" pb="0" />
			<FlexLayout
				display="flex"
				alignItems="center"
				gridGap="0.938rem"
				mt="2.625rem"
			>
				<Controller
					name="revision_section"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={watchRevision ? true : false}
								{...field}
								id="Status"
								defaultChecked={false}
							/>
							<LabelStyle>Revision section</LabelStyle>
						</FlexLayout>
					)}
				/>
			</FlexLayout>
			<FlexLayout
				display="flex"
				alignItems="center"
				gridGap="0.938rem"
				mt="1.188rem"
			>
				<Controller
					name="exams_section"
					control={control}
					render={({ field }) => (
						<FlexLayout
							display="flex"
							alignItems="center"
							gridGap="0.938rem"
							mt="0.625rem"
						>
							<StatusBox
								checked={watchExam ? true : false}
								{...field}
								id="Status"
								defaultChecked={false}
							/>
							<LabelStyle>Exam section</LabelStyle>
						</FlexLayout>
					)}
				/>
			</FlexLayout>
			<Divider label="Messaging" />
			<EdFormControl
				title="SMS provider"
				label={'SMS provider'}
				select
				control={control}
				name="sms_provider"
				helperText="Gateway for OTP and notification SMS. Takes up to a minute to take effect."
			>
				<MenuItem value="SMS_MISR">SMS Misr</MenuItem>
				<MenuItem value="CEQUENS">Cequens</MenuItem>
			</EdFormControl>
			<EdFormControl
				control={control}
				type="text"
				name="cequens_username"
				label="Cequens username"
				helperText="Only used when Cequens is the selected provider."
			/>
			<EdFormControl
				control={control}
				type="text"
				name="cequens_api_key"
				label="Cequens API key"
			/>
			<EdFormControl
				control={control}
				type="text"
				name="cequens_sender_name"
				label="Cequens sender name"
				helperText="Must be a sender ID registered with Cequens, or every send fails."
			/>
			<Divider label="Payment Methods" />
			<SectionLayout
				gridGap={'2.188rem !important'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<FlexLayout
					display="flex"
					alignItems="center"
					gridGap="0.938rem"
					width="100%"
				>
					<Controller
						name="recharge_card"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchRecharge ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Recharge Card</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout
					display="flex"
					alignItems="center"
					gridGap="0.938rem"
					width="100%"
				>
					<Controller
						name="credit_card_accept"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchCredit ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Credit Card Accept</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="fawry_fawry"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchFawry ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Fawry Fawry</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="ewallet_accept"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchWallet ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>E Wallet Accept</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="ewallet_fawry"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchWalletFawry ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>E Wallet Fawry</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="credit_card_opay"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchOpay ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Credit Card Opay</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="aman_accept"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchAman ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Aman Accept</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="ewallet_opay"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchWalletOpay ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>E Wallet Opay</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="masary_accept"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchMasay ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Masary Accept</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="fawry_moneyhash"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchFawryMoneyHash ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Fawry MoneyHash</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="credit_card_moneyhash"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchCardMoneyHash ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Credit Card MoneyHash</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="ewallet_moneyhash"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchWalletMoneyHash ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>E Wallet MoneyHash</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="credit_card_kashier"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchCardKashier ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>Credit Card Kashier</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
					<Controller
						name="ewallet_kashier"
						control={control}
						render={({ field }) => (
							<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
								<StatusBox
									checked={watchWalletKashier ? true : false}
									{...field}
									id="Status"
									defaultChecked={false}
								/>
								<LabelStyle>E Wallet Kashier</LabelStyle>
							</FlexLayout>
						)}
					/>
				</FlexLayout>
				<Controller
					name="manual_vc_enabled"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								{...field}
								checked={Boolean(field.value)}
								id="manual-vc-enabled"
							/>
							<LabelStyle as="label" htmlFor="manual-vc-enabled">
								VC Payment (Manual)
							</LabelStyle>
						</FlexLayout>
					)}
				/>
			</SectionLayout>
			<EdFormControl
				control={control}
				name="manual_vc_receiver"
				label="Manual VC receiver wallet number"
				type="tel"
			/>
			{/* MoneyHash selected_method_id per method (DB columns keep their legacy
			    moneyhash_flow_id_* names — repurposed, no migration). An id locks the
			    embed to that method; empty falls back to payment_method+form_only,
			    which white-screens CARD on the current account — so Card should
			    always have its id set. Ids are account+mode scoped, from MoneyHash.
			    Takes up to 60s to apply (the student backend caches settings). */}
			<EdFormControl
				control={control}
				name="moneyhash_flow_id_fawry"
				label="MoneyHash Method ID (Pay at Fawry)"
				helperText="selected_method_id from MoneyHash — locks the embed to this method. Empty = generic preselection."
			/>
			<EdFormControl
				control={control}
				name="moneyhash_flow_id_card"
				label="MoneyHash Method ID (Credit Card)"
				helperText="selected_method_id from MoneyHash — required for card (generic preselection white-screens)."
			/>
			<EdFormControl
				control={control}
				name="moneyhash_flow_id_ewallet"
				label="MoneyHash Method ID (E Wallet)"
				helperText="selected_method_id from MoneyHash — locks the embed to this method. Empty = generic preselection."
			/>
			<Divider label="Devices" />
			<EdFormControl
				control={control}
				// width="4.794rem"
				type="number"
				inputMode="numeric"
				name={`device_limit`}
				label="Trusted devices limit"
				required
			/>
			{/* Distinct from the device limit above: that caps how many devices are
			    REGISTERED, this caps how many sessions are usable at once. 0 means
			    unlimited. It replaced a boolean that nothing reads any more, so
			    until this field existed the cap could not be changed at all. */}
			<EdFormControl
				control={control}
				type="number"
				inputMode="numeric"
				name={`max_concurrent_sessions`}
				label="Concurrent sessions limit (0 = unlimited)"
			/>
			<FlexLayout
				display="flex"
				alignItems="center"
				gridGap="0.938rem"
				mt="1.5rem"
			>
				<Controller
					name="device_verification_required"
					control={control}
					render={({ field }) => (
						<FlexLayout
							display="flex"
							alignItems="center"
							gridGap="0.938rem"
							mt="0.625rem"
						>
							<StatusBox
								checked={watchDeviceVerification ? true : false}
								{...field}
								id="DeviceVerificationStatus"
								defaultChecked={false}
							/>
							{/* Turning this OFF does not turn the device limit off — devices
							    still register and the limit still applies. It only skips the
							    OTP, which is where the anti-sharing teeth actually are. */}
							<LabelStyle>New device verification (OTP) required</LabelStyle>
						</FlexLayout>
					)}
				/>
			</FlexLayout>
			<FlexLayout
				display="flex"
				alignItems="center"
				gridGap="0.938rem"
				mt="1rem"
			>
				<Controller
					name="device_limit_enforced"
					control={control}
					render={({ field }) => (
						<FlexLayout
							display="flex"
							alignItems="center"
							gridGap="0.938rem"
							mt="0.625rem"
						>
							<StatusBox
								checked={watchDeviceLimitEnforced ? true : false}
								{...field}
								id="DeviceLimitEnforcedStatus"
								defaultChecked={false}
							/>
							{/* OFF = the limit is measured but never blocks anyone, which is
							    how the number above gets tuned safely. Turning it ON blocks
							    registration of devices beyond the cap; eviction is
							    support-only, so a blocked student needs a human. Lowering
							    the cap never evicts anyone — it applies to the next
							    registration, not to devices a student already has. */}
							<LabelStyle>Enforce device limit (off = observe only)</LabelStyle>
						</FlexLayout>
					)}
				/>
			</FlexLayout>
			<Divider label="Theme" />
			<EdFormControl
				title="Theme"
				label={'Theme'}
				select
				control={control}
				name="theme"
			>
				<MenuItem value="primary">Primary</MenuItem>
				<MenuItem value="secondary">Secondary</MenuItem>
			</EdFormControl>

			<Divider label="Browser Tab" />
			<EdFormControl
				title="Browser tab title"
				label={'Browser tab title'}
				control={control}
				name="meta_title"
				helperText='Shown as the tab name, e.g. "abwaab | Admin Portal"'
			/>

			<Divider label="Course Settings" />
			<EdFormControl
				title="Video percentage"
				label={'Video percentage'}
				control={control}
				name="video_percentages"
				inputMode="numeric"
				type="number"
				required
				// width="4.794rem"
			/>
			{/* VdoCipher OTP TTL + allowed domains moved to
			    Settings → Video Providers (VdoCipher card). */}

			<Divider label="Home Page" />
			<SectionLayout
				gridGap={'2.188rem !important'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<EdFormControl
					label="Headline"
					control={control}
					name="landing_headline"
				/>
				<EdFormControl
					label="Sub Headline"
					control={control}
					name="landing_subheadline"
				/>
			</SectionLayout>

			<Divider label="Sections" />
			<SectionLayout
				gridGap={'2.188rem !important'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<Controller
					name="instructor_section"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={false}
							/>
							<LabelStyle>Instructors Section</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="subjects_section"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={false}
							/>
							<LabelStyle>Subjects Section</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="classrooms_enabled"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={true}
							/>
							<LabelStyle>Classrooms Platform</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="standalone_courses_enabled"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={false}
							/>
							<LabelStyle>Standalone Courses Platform</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="bundles_enabled"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={false}
							/>
							<LabelStyle>Bundles (requires Standalone Courses)</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="app_wallet"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={false}
							/>
							<LabelStyle>Wallet</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="waselchat_widget"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={false}
							/>
							<LabelStyle>WaselChat Student Chat</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="unit_notes_enabled"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={true}
							/>
							<LabelStyle>Unit Notes (student portal)</LabelStyle>
						</FlexLayout>
					)}
				/>
				<Controller
					name="unit_qa_enabled"
					control={control}
					render={({ field }) => (
						<FlexLayout display="flex" alignItems="center" gridGap="0.938rem">
							<StatusBox
								checked={field.value}
								{...field}
								defaultChecked={true}
							/>
							<LabelStyle>Unit Q&amp;A (student portal)</LabelStyle>
						</FlexLayout>
					)}
				/>
			</SectionLayout>
			<Divider label="Social Media" />
			<SectionLayout
				gridGap={'2.188rem !important'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<EdFormControl label="Facebook" control={control} name="fb_link" />
				<EdFormControl label="Youtube" control={control} name="youtube_link" />
				<EdFormControl
					label="Linkedin"
					control={control}
					name="linkedin_link"
				/>
				<EdFormControl
					label="Instagram"
					control={control}
					name="instagram_link"
				/>
			</SectionLayout>

			<Divider label="Terms & Privacy" />
			<SectionLayout
				gridGap={'2.188rem !important'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<EdFormControl
					label="Terms & Conditions"
					control={control}
					name="terms_link"
				/>
				<EdFormControl label="Privacy" control={control} name="privacy_link" />
			</SectionLayout>

			<FlexLayout justifyContent="flex-end" gridGap="0.8rem" mt="2rem">
				<Button
					variant="contained"
					color="warning"
					onClick={() => history.push('/')}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit(onSave, (e) => console.log(e))}
					variant="contained"
				>
					Save
				</Button>
			</FlexLayout>
		</SectionLayout>
	);
};

const SectionLayout = styled(GridLayout)`
	grid-gap: 2rem;
`;
const StatusBox = styled(Checkbox)`
	padding: 0;
	svg,
	input {
		width: 20px;
		height: 20px;
	}
`;
const LabelStyle = styled.label`
	font-size: 0.9rem;
	color: ${(props) => props.theme.palette.cadet.main};
	font-weight: normal;
	margin-top: 2px;
`;
const EdFormControlStyle = styled(EdFormControl)`
	.MuiInputBase-root {
		margin-inline-start: 3rem !important;
	}
`;

export default SettingsForm;
